import { useState } from "react";
import { useSelector } from "react-redux";
import useSWR from "swr";
import axios from "axios";
import Modal from "react-modal";
import { MdClose } from "react-icons/md";
import { FiMail, FiPhone, FiArrowLeft, FiGift, FiCopy, FiCheck } from "react-icons/fi";
import { ImSpinner2 } from "react-icons/im";
import fetcher from "../../utils/fetcher";
import mediaUrl from "../../utils/mediaUrl";
import defaultImg from "../../assets/register.webp";
import styles from "../../styles/offer.module.css";

const CLAIMED_KEY = "fzakr_offer_claimed";
const DISMISSED_KEY = "fzakr_offer_dismissed";
const DEFAULT_DESCRIPTION = "لوحات كانفاس إسلامية تُنير القلوب وتُزيّن البيوت";

// Storage can throw (private mode, blocked site data) — never let it break the page
const readFlag = (storage, key) => {
  try { return window[storage].getItem(key) === "1"; } catch { return false; }
};
const writeFlag = (storage, key) => {
  try { window[storage].setItem(key, "1"); } catch { /* ignore */ }
};

const OfferModal = ({ offer, isOpen, onClose, onClaimed }) => {
  const [email, setEmail]     = useState("");
  const [phone, setPhone]     = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [result, setResult]   = useState(null); // { promocode, discount }
  const [copied, setCopied]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/offer/claim`, {
        email: email.trim(),
        phone: phone.trim(),
        consent,
      });
      setResult(res.data?.data);
      onClaimed();
    } catch (err) {
      const status = err?.response?.status;
      const msg = String(err?.response?.data?.message ?? "");
      setError(
        status === 429
          ? "محاولات كثيرة. حاول مرة أخرى بعد دقيقة."
          : msg.includes("phone")
            ? "رقم الهاتف غير صحيح"
            : msg.includes("email")
              ? "البريد الإلكتروني غير صحيح"
              : msg.includes("not available")
                ? "العرض غير متاح حالياً"
                : "حدث خطأ. حاول مرة أخرى."
      );
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(result.promocode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the code is still visible */ }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.hero}>
        <img src={offer.imageUrl ? mediaUrl(offer.imageUrl) : defaultImg} alt="" className={styles.heroImg} />
        <button type="button" className={styles.close} onClick={onClose} aria-label="إغلاق">
          <MdClose />
        </button>
      </div>

      <div className={styles.body}>
        <h2 className={styles.title}>انضمُّوا إلى عائلة فَذَكِّر</h2>

        <div className={styles.ornament} aria-hidden="true">
          <span className={styles.ornamentLine} />
          <span className={styles.ornamentDiamond} />
          <span className={styles.ornamentLine} />
        </div>

        <p className={styles.subtitle}>واحصلوا على {offer.discount}% خصم على أول طلبية</p>
        <p className={styles.desc}>{offer.description || DEFAULT_DESCRIPTION}</p>

        {result ? (
          <div className={styles.success}>
            <p className={styles.successText}>شكراً لانضمامك! هذا كود الخصم الخاص بك:</p>
            <button type="button" className={styles.code} onClick={copyCode}>
              <span className={styles.codeText}>{result.promocode}</span>
              {copied ? <FiCheck className={styles.codeIcon} /> : <FiCopy className={styles.codeIcon} />}
            </button>
            <p className={styles.successHint}>
              {copied ? "تم النسخ ✓" : "اضغط لنسخ الكود، واستخدمه عند إتمام الطلب"}
            </p>
            <button type="button" className={styles.submit} onClick={onClose}>
              ابدأ التسوق
              <FiArrowLeft className={styles.submitIcon} />
            </button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <label className={styles.label} htmlFor="offer-email">
              البريد الإلكتروني<span className={styles.req}>*</span>
            </label>
            <div className={styles.field}>
              <input
                id="offer-email"
                type="email"
                className={styles.input}
                placeholder="you@email.com"
                maxLength={100}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <FiMail className={styles.fieldIcon} />
            </div>

            <label className={styles.label} htmlFor="offer-phone">
              رقم الهاتف<span className={styles.req}>*</span>
            </label>
            <div className={styles.field}>
              <input
                id="offer-phone"
                type="tel"
                className={styles.input}
                placeholder="05X-XXXXXXX"
                maxLength={20}
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <FiPhone className={styles.fieldIcon} />
            </div>

            <label className={styles.consent}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span className={styles.consentText}>أوافق على تلقي رسائل وعروض من فَذَكِّر</span>
            </label>

            {error && <p className={styles.error}>{error}</p>}

            <button type="submit" className={styles.submit} disabled={loading}>
              {loading ? <ImSpinner2 className={styles.spinner} /> : <>
                احصل على الخصم
                <FiArrowLeft className={styles.submitIcon} />
              </>}
            </button>
          </form>
        )}
      </div>
    </Modal>
  );
};

// Bar above the navbar (home page, guests only) that opens the offer modal
const WelcomeOffer = () => {
  const user = useSelector((state) => state.auth.user);
  const [open, setOpen] = useState(false);
  // Decided once on load: already claimed, or dismissed this session → don't fetch at all
  const [skip] = useState(
    () => readFlag("localStorage", CLAIMED_KEY) || readFlag("sessionStorage", DISMISSED_KEY)
  );
  const [barHidden, setBarHidden] = useState(skip);

  const { data: offer } = useSWR(
    !user && !skip ? `${process.env.REACT_APP_BACKEND_URL}/api/offer` : null,
    fetcher,
    { revalidateOnFocus: false, errorRetryCount: 1, dedupingInterval: 60000 }
  );

  if (!offer?.active) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    writeFlag("sessionStorage", DISMISSED_KEY);
    setBarHidden(true);
  };

  return (
    <>
      {!barHidden && !user && (
        <div
          className={styles.bar}
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(true)}
        >
          <FiGift className={styles.barIcon} />
          <span className={styles.barText}>{offer.barText}</span>
          <span className={styles.barBadge}>خصم {offer.discount}%</span>
          <button type="button" className={styles.barClose} onClick={dismiss} aria-label="إخفاء">
            <MdClose />
          </button>
        </div>
      )}

      <OfferModal
        offer={offer}
        isOpen={open}
        onClose={() => setOpen(false)}
        onClaimed={() => {
          writeFlag("localStorage", CLAIMED_KEY);
          setBarHidden(true);
        }}
      />
    </>
  );
};

export default WelcomeOffer;
